import React, {useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Section, Cell, Spinner, Input } from '@telegram-apps/telegram-ui';
import { BackButton } from '../components/BackButton';
import { MainButton } from '../components/MainButton';
import { useCart } from '../context/CartContext';

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Get date plus days
const getDatePlusDays = (dateString: string, days: number) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

interface Booking {
    start: string;
    end: string;
}

interface CatalogItem {
    id: number;
    title: string;
    image: string | null;
    price: number;
    price_2days: number | null;
    price_3to6days: number | null;
    price_over7days: number | null;
    deposit: number;
    min_period: number | null;
    description_short: string | null;
    actual_bookings: Booking[];
}

// Format date for display
const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rentstation.ru';

function ItemPage() {
    const { itemId } = useParams<{ itemId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { addItem, loading: cartLoading } = useCart();
    const [item, setItem] = useState<CatalogItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);

    // Initialize dates from URL params or defaults
    const [startDate, setStartDate] = useState(searchParams.get('start') || getTodayDate());
    const [endDate, setEndDate] = useState(searchParams.get('end') || getDatePlusDays(getTodayDate(), 7));

    const handleBook = useCallback(async () => {
        if (!itemId || adding) return;

        setAdding(true);
        try {
            await addItem(parseInt(itemId), startDate, endDate);
            navigate('/cart');
        } catch (err: any) {
            setError(err.message || 'Failed to add item to cart');
        } finally {
            setAdding(false);
        }
    }, [itemId, startDate, endDate, addItem, navigate, adding]);

    useEffect(() => {
        if (!itemId) return;

        fetch(`${API_BASE_URL}/api/catalog/${itemId}`)
            .then((res) => {
                if (!res.ok) throw new Error('Item not found');
                return res.json();
            })
            .then((data: CatalogItem) => {
                setItem(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [itemId]);

    if (loading) {
        return (
            <Section>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Spinner size="m" />
                </div>
            </Section>
        );
    }

    if (error || !item) {
        return (
            <>
            <BackButton />
            <Section header="Ошибка">
                <Cell>{error || 'Item not found'}</Cell>
            </Section>
            </>
        );
    }

    const buttonText = adding ? 'Добавляем...' : 'Забронировать';

    return (
        <>
            <BackButton />
            <MainButton text={buttonText} onClick={handleBook} visible={!!item} loading={adding || cartLoading} />
            <Section>
                {item.image && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                        <img
                            src={item.image}
                            alt={item.title}
                            style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: 12 }}
                        />
                    </div>
                )}
                <Cell subtitle="Название">{item.title}</Cell>
                {item.description_short && (
                    <Cell subtitle="Описание">{item.description_short}</Cell>
                )}
            </Section>
            <Section header="Цена">
                <Cell>{item.price} ₽/день</Cell>
                {(item.price_2days ?? 0) > 0 && (
                    <Cell subtitle="2 дня">{item.price_2days} ₽/день</Cell>
                )}
                {(item.price_3to6days ?? 0) > 0 && (
                    <Cell subtitle="3-6 дней">{item.price_3to6days} ₽/день</Cell>
                )}
                {(item.price_over7days ?? 0) > 0 && (
                    <Cell subtitle="7+ дней">{item.price_over7days} ₽/день</Cell>
                )}
                {item.deposit > 0 && (
                    <Cell subtitle="Залог">{item.deposit} ₽</Cell>
                )}
                {item.min_period === 1 && (
                    <Cell subtitle="Мин. срок">Неделя</Cell>
                )}
                {item.min_period === 2 && (
                    <Cell subtitle="Мин. срок">Месяц</Cell>
                )}
            </Section>
            {item.actual_bookings && item.actual_bookings.length > 0 && (
                <Section header="Занят в даты">
                    {item.actual_bookings.map((booking, index) => (
                        <Cell key={index}>
                            {formatDate(booking.start)} – {formatDate(booking.end)}
                        </Cell>
                    ))}
                </Section>
            )}
            <Section>
                <Input
                    header="Забрать"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                    header="Вернуть"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />
            </Section>
        </>
    );
}

export default ItemPage;
